import { ScryfallClient } from "@/lib/scryfall";
import type { ScryfallCard } from "@/types/scryfall";

export interface CardIdentifier {
  count: number;
  name: string;
  set?: string;
  collector_number?: string;
  is_foil: string;
}

export const parseCardList = (list: string) => {
  const lines = list.split('\n').filter(line => line.trim() !== '');
  let currentSection = 'main'; // main, sideboard, commander, companion

  const sections: Record<string, CardIdentifier[]> = {
    main: [],
    sideboard: [],
    commander: [],
    companion: [],
  };

  lines.forEach(line => {
    const trimmedLine = line.trim();
    const lowerLine = trimmedLine.toLowerCase();

    // Check for various sideboard indicators (including commented ones)
    if (lowerLine.startsWith('sideboard') ||
        lowerLine.startsWith('sb:') ||
        lowerLine === 'sideboard:' ||
        lowerLine === 'sb' ||
        lowerLine.match(/^\/\/\s*sideboard/)) {
      currentSection = 'sideboard';

      // Extract any card that might be on the same line after the sideboard indicator
      let cardPart = '';
      if (lowerLine.startsWith('sideboard:')) {
        cardPart = trimmedLine.substring(trimmedLine.indexOf(':') + 1).trim();
      } else if (lowerLine.startsWith('sideboard ')) {
        cardPart = trimmedLine.substring(10).trim();
      } else if (lowerLine.startsWith('sb:')) {
        cardPart = trimmedLine.substring(3).trim();
      }

      // If there's a card on the same line, we'll process it in the next iteration
      if (cardPart) {
        // Process the card part immediately instead of modifying the array
        let name = cardPart;
        let set, collector_number;

        const setMatch = cardPart.match(/\s\((\w{3,6})\)\s+(.+)$/);
        if (setMatch) {
          set = setMatch[1].toLowerCase();
          collector_number = setMatch[2];
          name = cardPart.substring(0, setMatch.index).trim();
        }

        let count = 1;
        const countMatch = name.match(/^(\d+)\s*[xX]?\s*/);
        if (countMatch) {
          count = parseInt(countMatch[1], 10);
          name = name.substring(countMatch[0].length).trim();
        }

        const foil = name.includes('*F*') || (collector_number && collector_number.includes('*F*'));
        if (foil) {
          name = name.replace(/\*F\*/g, '').trim();
          if (collector_number) {
            collector_number = collector_number.replace(/\*F\*/g, '').trim();
          }
        }

        if (collector_number) {
          collector_number = collector_number.replace(/★/g, '').trim();
        }
        name = name.replace(/★/g, '').trim();

        if (name) {
          sections[currentSection].push({
            count,
            name: name.trim(),
            set,
            collector_number,
            is_foil: foil ? 'foil' : '',
          });
        }
      }
      return;
    }

    // Skip regular comment lines (but not sideboard indicators)
    if (lowerLine.startsWith('//') && !lowerLine.match(/^\/\/\s*sideboard/)) return;
    if (lowerLine === 'commander') {
      currentSection = 'commander';
      return;
    }
    if (lowerLine === 'companion') {
      currentSection = 'companion';
      return;
    }

    let name = trimmedLine;
    let set, collector_number;

    const setMatch = trimmedLine.match(/\s\((\w{3,6})\)\s+(.+)$/);
    if (setMatch) {
      set = setMatch[1].toLowerCase();
      collector_number = setMatch[2];
      name = trimmedLine.substring(0, setMatch.index).trim();
    }

    let count = 1;
    const countMatch = name.match(/^(\d+)\s*[xX]?\s*/);
    if (countMatch) {
      count = parseInt(countMatch[1], 10);
      name = name.substring(countMatch[0].length).trim();
    }

    const foil = name.includes('*F*') || (collector_number && collector_number.includes('*F*'));
    if (foil) {
      name = name.replace(/\*F\*/g, '').trim();
      if (collector_number) {
        collector_number = collector_number.replace(/\*F\*/g, '').trim();
      }
    }

    if (collector_number) {
      collector_number = collector_number.replace(/★/g, '').trim();
    }
    name = name.replace(/★/g, '').trim();

    if (name) {
      sections[currentSection].push({
        count,
        name: name.trim(),
        set,
        collector_number,
        is_foil: foil ? 'foil' : '',
      });
    }
  });

  return sections;
};

const chunk = <T>(array: T[], size: number): T[][] => {
  const chunked_arr: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunked_arr.push(array.slice(i, i + size));
  }
  return chunked_arr;
};

export const fetchCardsFromIdentifiers = async (identifiers: CardIdentifier[], apiBaseUrl: string) => {
  const scryfall = new ScryfallClient(apiBaseUrl);
  const importedCards: { card: ScryfallCard, identifier: CardIdentifier }[] = [];
  let remainingIdentifiers = [...identifiers];



  // First pass: by set and collector number
  const withSetAndNumber = remainingIdentifiers.filter(id => id.set && id.collector_number);
  if (withSetAndNumber.length > 0) {
    const chunks = chunk(withSetAndNumber, 75);
    for (const c of chunks) {
      const scryfallIdentifiers = c.map(({ set, collector_number }) => ({ set, collector_number }));
      const results = await scryfall.getCardCollection(scryfallIdentifiers);

      results.forEach(card => {
        if ((card as ScryfallCard).object !== 'error') {
          const identifier = remainingIdentifiers.find(id => id.set === card.set && id.collector_number === card.collector_number);
          if (identifier) {
            importedCards.push({ card, identifier });
          }
        }
      });
    }
  }

  remainingIdentifiers = identifiers.filter(id => !importedCards.some(ic => ic.identifier === id));

  // Second pass: by name for remaining
  if (remainingIdentifiers.length > 0) {
    const chunks = chunk(remainingIdentifiers, 75);
    for (const c of chunks) {
      const scryfallIdentifiers = c.map(({ name }) => ({ name }));
      const results = await scryfall.getCardCollection(scryfallIdentifiers);

      results.forEach(card => {
        if ((card as ScryfallCard).object !== 'error') {
          const identifier = remainingIdentifiers.find(id => id.name.toLowerCase() === card.name.toLowerCase());
          if (identifier) {
            importedCards.push({ card, identifier });
            remainingIdentifiers = remainingIdentifiers.filter(id => id !== identifier);
          }
        }
      });
    }
  }

  const finalCards: ScryfallCard[] = [];
  importedCards.forEach(({ card, identifier }) => {
    for (let i = 0; i < identifier.count; i++) {
      finalCards.push({ ...card, is_foil: identifier.is_foil });
    }
  });

  if (remainingIdentifiers.length > 0) {
    console.warn('Could not find cards for identifiers:', remainingIdentifiers);
  }

  return finalCards;
};
